/**
 * Theme Colors - Light and dark theme color definitions for icons
 *
 * This module provides color mappings for each icon type in both light and dark themes,
 * including support for highlighted states and risk severity levels.
 */

import { IconType, ThemeColors } from '@/components/icons/nodeIcons/types';
import { RiskSeverity } from '@/types';

/**
 * Severity color configuration for risk icons
 * Maps risk severity levels to background and stroke colors for each theme
 */
export const severityColors = {
  light: {
    [RiskSeverity.Critical]: {
      background: 'rgb(255, 89, 89)',
      stroke: 'rgb(255, 1, 1)',
    },
    [RiskSeverity.High]: {
      background: 'rgb(242, 150, 98)',
      stroke: 'rgb(235, 94, 14)',
    },
    [RiskSeverity.Medium]: {
      background: 'rgb(255, 229, 89)',
      stroke: 'rgb(255, 215, 0)',
    },
    [RiskSeverity.Low]: {
      background: 'rgb(230, 230, 230)',
      stroke: 'rgb(217, 217, 217)',
    },
    [RiskSeverity.Info]: {
      background: 'rgb(100, 209, 173)',
      stroke: 'rgb(13, 148, 103)',
    },
    [RiskSeverity.Exposure]: {
      background: 'rgb(122, 8, 21)',
      stroke: 'rgb(169, 0, 0)',
    },
  },
  dark: {
    [RiskSeverity.Critical]: {
      background: 'rgb(184, 22, 40)',
      stroke: 'rgb(255, 1, 1)',
    },
    [RiskSeverity.High]: {
      background: 'rgb(199, 85, 35)',
      stroke: 'rgb(235, 94, 14)',
    },
    [RiskSeverity.Medium]: {
      background: 'rgb(130, 113, 46)',
      stroke: 'rgb(255, 215, 0)',
    },
    [RiskSeverity.Low]: {
      background: 'rgb(65, 64, 88)',
      stroke: 'rgb(217, 217, 217)',
    },
    [RiskSeverity.Info]: {
      background: 'rgb(30, 88, 86)',
      stroke: 'rgb(16, 185, 129)',
    },
    [RiskSeverity.Exposure]: {
      background: 'rgb(122, 8, 21)',
      stroke: 'rgb(169, 0, 0)',
    },
  },
};

/**
 * Get computer icon colors based on theme and highlight state
 */
const getComputerColors = (
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean
): ThemeColors => ({
  background: isHighlighted
    ? currentTheme === 'light'
      ? 'rgb(95, 71, 183)'
      : 'rgb(122, 90, 241)'
    : currentTheme === 'light'
      ? 'rgb(230, 230, 230)'
      : 'rgb(65, 64, 88)',
  stroke: isHighlighted
    ? currentTheme === 'light'
      ? 'rgb(95, 71, 183)'
      : 'rgb(122, 90, 241)'
    : currentTheme === 'light'
      ? 'rgb(217, 217, 217)'
      : '#D9D9D9',
  icon: currentTheme === 'light' ? 'rgb(131, 129, 139)' : 'rgb(187, 185, 195)',
});

/**
 * Get attribute icon colors based on theme and highlight state
 */
const getAttributeColors = (
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean
): ThemeColors => ({
  background:
    currentTheme === 'light' ? 'rgb(236, 233, 251)' : 'rgb(40, 32, 90)',
  stroke: isHighlighted
    ? currentTheme === 'light'
      ? 'rgb(122, 90, 241)'
      : 'rgb(122, 90, 241)'
    : currentTheme === 'light'
      ? 'rgb(95, 71, 183)'
      : 'rgb(122, 90, 241)',
  icon: currentTheme === 'light' ? 'rgb(131, 129, 139)' : 'rgb(187, 185, 195)',
});

/**
 * Get seed/technology icon colors based on theme and highlight state
 */
const getSeedTechnologyColors = (
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean
): ThemeColors => {
  const background =
    currentTheme === 'light' ? 'rgb(239, 246, 255)' : 'rgb(30, 58, 138)';
  const stroke =
    currentTheme === 'light' ? 'rgb(59, 130, 246)' : 'rgb(96, 165, 250)';
  return {
    background: isHighlighted ? stroke : background,
    stroke,
    icon:
      currentTheme === 'light' ? 'rgb(131, 129, 139)' : 'rgb(187, 185, 195)',
  };
};

/**
 * Get tag icon colors based on theme
 */
const getTagColors = (currentTheme: 'light' | 'dark'): ThemeColors => ({
  background:
    currentTheme === 'light' ? 'rgb(240, 253, 244)' : 'rgb(22, 101, 52)',
  stroke: currentTheme === 'light' ? 'rgb(34, 197, 94)' : 'rgb(74, 222, 128)',
  icon: currentTheme === 'light' ? 'rgb(34, 197, 94)' : 'rgb(74, 222, 128)',
});

const getUserColors = (
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean
): ThemeColors => {
  const baseBackground =
    currentTheme === 'light' ? 'rgb(193, 214, 255)' : 'rgb(39, 70, 190)';
  const baseStroke =
    currentTheme === 'light' ? 'rgb(59, 130, 246)' : 'rgb(129, 178, 255)';
  return {
    background: isHighlighted ? baseStroke : baseBackground,
    stroke: baseStroke,
    icon: currentTheme === 'light' ? 'rgb(30, 78, 176)' : 'rgb(205, 222, 255)',
  };
};

const getGroupColors = (
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean
): ThemeColors => {
  const baseBackground =
    currentTheme === 'light' ? 'rgb(232, 210, 255)' : 'rgb(75, 35, 159)';
  const baseStroke =
    currentTheme === 'light' ? 'rgb(171, 85, 247)' : 'rgb(196, 146, 255)';
  return {
    background: isHighlighted ? baseStroke : baseBackground,
    stroke: baseStroke,
    icon: currentTheme === 'light' ? 'rgb(102, 52, 184)' : 'rgb(229, 218, 255)',
  };
};

const getDomainColors = (
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean
): ThemeColors => {
  const baseBackground =
    currentTheme === 'light' ? 'rgb(213, 245, 223)' : 'rgb(25, 85, 55)';
  const baseStroke =
    currentTheme === 'light' ? 'rgb(74, 222, 128)' : 'rgb(134, 239, 172)';
  return {
    background: isHighlighted ? baseStroke : baseBackground,
    stroke: baseStroke,
    icon: currentTheme === 'light' ? 'rgb(31, 94, 58)' : 'rgb(209, 250, 229)',
  };
};

const getCloudColors = (
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean
): ThemeColors => {
  // AWS orange/cloud-themed colors
  const baseBackground =
    currentTheme === 'light' ? 'rgb(255, 237, 213)' : 'rgb(120, 53, 15)';
  const baseStroke =
    currentTheme === 'light' ? 'rgb(251, 146, 60)' : 'rgb(253, 186, 116)';
  return {
    background: isHighlighted ? baseStroke : baseBackground,
    stroke: baseStroke,
    icon: currentTheme === 'light' ? 'rgb(194, 65, 12)' : 'rgb(254, 215, 170)',
  };
};

/**
 * Get webpage icon colors based on theme
 */
const getWebpageColors = (currentTheme: 'light' | 'dark'): ThemeColors => ({
  background:
    currentTheme === 'light' ? 'rgb(243, 240, 255)' : 'rgb(55, 48, 107)',
  stroke: currentTheme === 'light' ? 'rgb(139, 92, 246)' : 'rgb(167, 139, 250)',
  icon: currentTheme === 'light' ? 'rgb(139, 92, 246)' : 'rgb(167, 139, 250)',
});

/**
 * Get default icon colors based on theme
 */
const getDefaultColors = (currentTheme: 'light' | 'dark'): ThemeColors => ({
  background:
    currentTheme === 'light' ? 'rgb(236, 233, 251)' : 'rgb(40, 32, 90)',
  stroke: currentTheme === 'light' ? 'rgb(95, 71, 183)' : 'rgb(122, 90, 241)',
  icon: currentTheme === 'light' ? 'rgb(131, 129, 139)' : 'rgb(187, 185, 195)',
});

/**
 * Get theme colors for a specific icon type
 *
 * @param type - The icon type
 * @param currentTheme - Current theme (light or dark)
 * @param isHighlighted - Whether the icon is in highlighted state
 * @param severity - Risk severity level (only for risk icons)
 * @returns Theme colors for the icon
 */
export const getThemeColors = (
  type: IconType,
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean = false,
  severity?: RiskSeverity
): ThemeColors => {
  // Special handling for risk icons with severity
  if (type === 'risk' && severity) {
    const severityColor = severityColors[currentTheme][severity];
    return {
      background: isHighlighted
        ? severityColor.stroke
        : severityColor.background,
      stroke: severityColor.stroke,
      icon:
        currentTheme === 'light' ? 'rgb(131, 129, 139)' : 'rgb(187, 185, 195)',
    };
  }

  // Standard icon type colors
  switch (type) {
    case 'computer':
      return getComputerColors(currentTheme, isHighlighted);
    case 'attribute':
      return getAttributeColors(currentTheme, isHighlighted);
    case 'seed':
    case 'technology':
      return getSeedTechnologyColors(currentTheme, isHighlighted);
    case 'tag':
      return getTagColors(currentTheme);
    case 'webpage':
      return getWebpageColors(currentTheme);
    case 'user':
      return getUserColors(currentTheme, isHighlighted);
    case 'group':
      return getGroupColors(currentTheme, isHighlighted);
    case 'domain':
      return getDomainColors(currentTheme, isHighlighted);
    case 'cloud':
      return getCloudColors(currentTheme, isHighlighted);
    default:
      return getDefaultColors(currentTheme);
  }
};
